import { cn } from "@/lib/utils";

export type SpinnerSize = "sm" | "md" | "lg";

interface SpinnerProps {
  className?: string;
  size?: SpinnerSize;
}

export const SPINNER_LOGO_PX: Record<SpinnerSize, number> = {
  sm: 32,
  md: 48,
  lg: 64,
};

/** Absorbs antialiasing bleed at the rotation extrema. */
const FRAME_ANTIALIAS_PX = 2;

/**
 * The logo is a square, so rotating it sweeps a circle of diameter size * √2.
 * The frame must reserve that diameter or an ancestor with overflow hidden,
 * auto, or clip cuts the corners part-way through each turn. Deriving the frame
 * from the logo keeps the two from drifting apart as sizes change.
 */
export function spinnerFramePx(size: SpinnerSize): number {
  return Math.ceil(SPINNER_LOGO_PX[size] * Math.SQRT2) + FRAME_ANTIALIAS_PX;
}

export default function Spinner({ className, size = "md" }: SpinnerProps) {
  const framePx = spinnerFramePx(size);
  const logoPx = SPINNER_LOGO_PX[size];

  return (
    <div
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-visible",
        className,
      )}
      style={{ width: framePx, height: framePx }}
      data-slot="spinner-frame"
    >
      <div
        className="origin-center animate-smooth-spin"
        style={{ width: logoPx, height: logoPx }}
        data-slot="spinner-logo"
      >
        <svg
          viewBox="0 0 80 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block size-full overflow-visible"
          role="img"
          aria-label="Loading spinner"
        >
          <title>Loading spinner</title>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3 47.0001L77 27.0001L78 33.0001L4 53.0001L3 47.0001Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M59.9999 78.0002L40 3.00012L46 3.00015L66 77.0001L59.9999 78.0002Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M33.9999 77.0001L14 2.00016L20.0001 1.00012L39.9998 76.0002L33.9999 77.0001Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M74 6.00012H6V74.0001H74V6.00012ZM0 0.00012207V80.0001H80V0.00012207H0Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </div>
  );
}
